import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { updateEntryRouteSilently } from "src/generator/utils/navigateToLogbookEntry";
import { generatorStore } from "src/generatorV2/store";
import { usePostApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { selectIsAutosaveBlocked } from "src/redux/selectors/selectIsAutosaveBlocked";
import {
  setAutoSaving,
  setCurrent,
  setReleaseEntryLock,
} from "src/redux/slices/entrySlice";
import { setSendDataOnNewEntry } from "src/redux/slices/generatorSlice";
import { getCurrentUTC } from "src/utils/timeUtils/getCurrentUTC";

import { useEntryNavigation } from "./useEntryNavigation";
import { useEntryParams } from "./useEntryParams";

interface UseAutosaveOptions {
  enableAutosave?: boolean;
  autosaveDelayMs?: number;
}

/**
 * V2 Autosave - triggers after 5s of inactivity, then releases lock
 */
export const useAutosave = (
  params: ReturnType<typeof useEntryParams>,
  _navigation: ReturnType<typeof useEntryNavigation>,
  options: UseAutosaveOptions = {},
) => {
  const { enableAutosave = false } = options;
  const { post } = usePostApi();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const isDirtyRef = useRef(false);
  const isAutosaveBlocked = useSelector(selectIsAutosaveBlocked);

  const {
    getCommonParams,
    getRouteEntryId,
    current,
    entryIdType,
    isReadOnly,
    isAutoSaving,
    isNewEntry,
    editing,
    isDirty,
    logbookId,
  } = params;

  const isAutosaveBlockedRef = useRef(isAutosaveBlocked);

  const openedSaveModal = useSelector(
    state => state.entrySlice.openedSaveModal,
  );

  useEffect(() => {
    isAutosaveBlockedRef.current = isAutosaveBlocked;
  }, [isAutosaveBlocked]);

  // Track previous editing state
  const prevEditingRef = useRef(editing);

  const autoSaveExistingEntry = useCallback(async () => {
    const baseParams = getCommonParams();
    const entryId = getRouteEntryId();
    if (!baseParams || !entryId) {
      dispatch(setReleaseEntryLock(Math.random()));
      return;
    }

    dispatch(setAutoSaving({ isSaving: true }));
    try {
      const result = await post({
        request: "auto-save-entry",
        body: {
          params: {
            _current: current,
            autosave: true,
            ...baseParams,
            [entryIdType]: entryId,
          },
        },
      });

      dispatch(setAutoSaving({ isSaving: false }));

      if (result) {
        dispatch(setCurrent(result.current || getCurrentUTC()));
        // On successful save, clear the dirty flag
        generatorStore.getState().setDirty(false);
      }

      // Always release lock after autosave attempt (success or fail)
      dispatch(setReleaseEntryLock(Math.random()));
    } catch {
      dispatch(setAutoSaving({ isSaving: false, isError: true }));
      // Release lock even on error
      dispatch(setReleaseEntryLock(Math.random()));
    }
  }, [current, dispatch, entryIdType, getCommonParams, getRouteEntryId, post]);

  const autoSaveNewEntry = useCallback(async () => {
    const baseParams = getCommonParams();
    if (!baseParams) {
      dispatch(setReleaseEntryLock(Math.random()));
      return;
    }

    dispatch(setAutoSaving({ isSaving: true }));
    try {
      const result = await post({
        request: "draft-save-entry",
        body: {
          params: {
            autosave: true,
            ...baseParams,
            [entryIdType]: "new",
          },
        },
      });

      dispatch(setAutoSaving({ isSaving: false }));

      if (result) {
        dispatch(setCurrent(result.current || getCurrentUTC()));
        generatorStore.getState().setDirty(false);
        dispatch(setSendDataOnNewEntry(true));
        const savedId =
          result?.[entryIdType] ??
          result?.lbeid ??
          result?.partid ??
          result?.meid;
        if (savedId && logbookId) {
          updateEntryRouteSilently(
            dispatch,
            navigate,
            setSearchParams,
            logbookId,
            entryIdType,
            savedId,
          );
        }
      }

      // Always release lock after autosave attempt
      dispatch(setReleaseEntryLock(Math.random()));
    } catch {
      dispatch(setAutoSaving({ isSaving: false, isError: true }));
      // Release lock even on error
      dispatch(setReleaseEntryLock(Math.random()));
    }
  }, [
    dispatch,
    entryIdType,
    getCommonParams,
    logbookId,
    navigate,
    post,
    setSearchParams,
  ]);

  // Autosave trigger
  const performAutosave = useCallback(async () => {
    if (isReadOnly || isAutoSaving) {
      // If already saving (e.g. manual save in progress),
      // let that save process release the lock.
      return;
    }

    if (isNewEntry) {
      await autoSaveNewEntry();
    } else {
      await autoSaveExistingEntry();
    }
  }, [
    autoSaveExistingEntry,
    autoSaveNewEntry,
    isAutoSaving,
    isNewEntry,
    isReadOnly,
  ]);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // Inactivity-based autosave
  useEffect(() => {
    if (!enableAutosave || isReadOnly || openedSaveModal || isAutosaveBlocked) {
      prevEditingRef.current = editing;
      return;
    }

    // Since store.ts waits 5s before setting editing=false,
    // we can just fire immediately when editing becomes false
    if (!editing && prevEditingRef.current) {
      if (isDirtyRef.current) {
        void performAutosave();
      } else {
        // If it's not dirty, no save is needed. Release lock immediately.
        dispatch(setReleaseEntryLock(Math.random()));
      }
    }

    // Always update previous state tracking at end of effect
    prevEditingRef.current = editing;
  }, [
    enableAutosave,
    isReadOnly,
    openedSaveModal,
    isAutosaveBlocked,
    editing,
    performAutosave,
    dispatch,
  ]);

  // No cleanup timer left, just return the functions
  return { autoSave: performAutosave, isSaving: isAutoSaving };
};
